import Label from '@/components/core/label';
import { ChoicesOrderItemType } from '@/sections/management/accounts/orders/context';
import { ChoicesOrderItemLabel } from '@/types/order';
import { RHFNumberText } from '@components/forms/hook-form/rhf-number-field';
import { CopyText } from '@components/utils/copy-text';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import {
  Card,
  Chip,
  Stack,
  Table,
  Avatar,
  Tooltip,
  Collapse,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  CardHeader,
  IconButton,
  Typography,
  CardContent,
} from '@mui/material';
import { t } from 'i18next';
import { useState } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { GroupedUniqueItem } from '../../utils/billing.utils';
import { BillingMilestonesByItemTable } from './billing-milestone-by-item-table';

// Define minimal milestone type required for the table
interface MinimalMilestone {
  _id: string;
  milestonePeriod: number;
  paymentDate: Date;
  total: number;
}

// Base item type that includes all fields used by BillingItemsTable
type BillingItemsTableItem = Omit<GroupedUniqueItem, 'milestones'> & {
  milestones: MinimalMilestone[];
};

interface BillingItemsTableProps {
  items: BillingItemsTableItem[];
}
export const BillingItemsTable = ({ items }: BillingItemsTableProps) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const handleToggleCollapse = (subscription: string, type: string) => {
    const key = `${subscription}-${type}`;
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };
  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader title={t('Items list')} />
      <CardContent sx={{ px: 0, '&:last-child': { pb: 0 } }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell width={60} align="center">
                {t('Item')}
              </TableCell>
              <TableCell>{t('Type')}</TableCell>
              <TableCell>{t('Product')}</TableCell>
              <TableCell>{t('SKU/Plan')}</TableCell>
              <TableCell align="center">{t('Provision Date')}</TableCell>
              <TableCell align="center">{t('Renewal Date')}</TableCell>
              <TableCell align="center">{t('Quantity')}</TableCell>

              <TableCell align="center" width={200}>
                {t('Total Amount')}
                <Tooltip
                  title={t(
                    `This amount represents the total of the contract; however, the amount to be billed may be lower. For more details, please expand this item and review all associated billing milestones.`
                  )}
                >
                  <InfoIcon
                    sx={{ fontSize: 16, marginLeft: 0.5, verticalAlign: 'text-top' }}
                    color="error"
                  />
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => {
              const isExpanded = expandedItems.has(`${item.subscription}-${item.type}`);
              return (
                <Fragment key={`product-list-${item.subscription}-${item.type}`}>
                  <TableRow>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell>
                      <Label
                        color={
                          item.type === ChoicesOrderItemType.newResource ? 'primary' : 'secondary'
                        }
                        variant="outlined"
                      >
                        {ChoicesOrderItemLabel[item.type]}
                      </Label>
                    </TableCell>

                    <TableCell>
                      <Typography variant="subtitle2">{item.productName}</Typography>
                      <Chip
                        avatar={<Avatar alt={item.productName} src={decodeURI(item.partnerLogo)} />}
                        variant="outlined"
                        size="small"
                        label={item.productName}
                      />
                    </TableCell>

                    <TableCell>
                      <Stack>
                        <Typography variant="subtitle2">{item.sku}</Typography>
                        <Typography variant="caption">{item.plan.name}</Typography>
                        <CopyText
                          text={item.plan.code}
                          color="text.secondary"
                          variant="caption"
                          sx={{ fontSize: 'inherit' }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="center">{item.provisionDate}</TableCell>
                    <TableCell align="center">{item.renewalDate}</TableCell>
                    <TableCell align="center">
                      <Label>{item.quantity}</Label>
                    </TableCell>
                    <TableCell align="center">
                      <RHFNumberText
                        value={item.totalAmount}
                        decimalScale={2}
                        fixedDecimalScale
                        prefix="$ "
                      />
                      <IconButton
                        onClick={() => handleToggleCollapse(item.subscription, item.type)}
                      >
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={9} style={{ padding: 0, border: 0 }}>
                      <Collapse
                        key={`product-list-${item.subscription}-${item.type}`}
                        in={isExpanded}
                        timeout="auto"
                        unmountOnExit
                      >
                        <BillingMilestonesByItemTable milestones={item.milestones} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
